import { Link } from 'react-router-dom';
import { useState } from 'react';
import { NerdStackFooter } from '../components/NerdStackFooter.jsx';
import { renderGlyph } from '../components/toolGlyphs.jsx';
import { gradientFor } from '../components/toolGradients.js';
import BackArrow from '../components/BackArrow.jsx';

// ─── Design tokens ─────────────────────────────────────────────────
const BG = '#0A0B14';
const CARD = '#14162A';
const BORDER = '#262A3F';
const TEXT = '#E2E8F0';
const MUTED = '#94A3B8';
const VIOLET = '#8B5CF6';
const VIOLET_LIGHT = '#A78BFA';
const FONT = "'DM Sans', system-ui, -apple-system, sans-serif";

// ─── Tool definitions ─────────────────────────────────────────────
// `state`: 'live' | 'soon' | 'suggest'
// `accent`: the tool's brand color (used for hover glow)
const TOOLS = [
  // Row 1 — free utility tools
  { name: 'GradeCalc',     path: '/stack/gradecalc', icon: '/icons/gradecalc-256.png',   accent: '#5EEAD4', state: 'live' },
  { name: 'StudyTimer',    path: '/stack/timer',     icon: '/icons/timer-256.png',       accent: '#F472B6', state: 'live' },
  { name: 'WordCount',     path: '/stack/wordcount', icon: '/icons/wordcount-256.png',   accent: '#6366F1', state: 'live' },
  // Row 2 — free utility tools
  { name: 'PercentCalc',   path: '/stack/percent',   icon: '/icons/percentcalc-256.png', accent: '#0EA5E9', state: 'live' },
  { name: 'UnitConvert',   path: '/stack/convert',   icon: '/icons/unitconvert-256.png', accent: '#84CC16', state: 'live' },
  { name: 'AverageCalc',   path: '/stack/average',   icon: '/icons/averagecalc-256.png', accent: '#F97316', state: 'live' },
  // Row 3 — AI tools (coming soon) + suggest tile
  { name: 'QuizCard',      path: '/stack/quizcard',  icon: '/icons/quizcard-256.png',    accent: VIOLET,   state: 'soon' },
  { name: 'Suggest a tool', path: '/suggest',  icon: null,                         accent: VIOLET,   state: 'suggest' },
  { name: 'ClearLens',     path: '/stack/clearlens', icon: '/icons/clearlens-256.png',   accent: '#F59E0B', state: 'soon' },
];

// ─── Tile component ────────────────────────────────────────────────
function Tile({ tool }) {
  const [hover, setHover] = useState(false);
  const isSoon = tool.state === 'soon';
  const isSuggest = tool.state === 'suggest';

  // Card styling shared across all tiles
  const cardStyle = {
    position: 'relative',
    aspectRatio: '1 / 1',
    background: CARD,
    border: `1px solid ${BORDER}`,
    borderRadius: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
    transform: hover ? 'translateY(-2px)' : 'translateY(0)',
    boxShadow: hover ? `0 12px 32px -8px ${tool.accent}33, 0 0 0 1px ${tool.accent}55` : 'none',
    borderColor: hover ? `${tool.accent}55` : BORDER,
    overflow: 'hidden',
  };

  // Glyph from the shared system. NerdStack tools + the Suggest mark
  // are keyed by tool.name in toolGlyphs; gradientFor resolves them to
  // family violet (their paths aren't suite-prefixed). This replaces
  // the old per-tool PNGs so NerdStack matches the 5 suites' language.
  const { from, to: gend, id } = gradientFor(tool.name, tool.path);
  const gdefs = (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor={from} />
        <stop offset="1" stopColor={gend} />
      </linearGradient>
    </defs>
  );
  const glyph = renderGlyph(tool.name, {
    size: 44,
    stroke: `url(#${id})`,
    strokeWidth: 1.9,
    defs: gdefs,
  });

  // Suggest tile keeps a distinct read (dashed border = "add new, not a
  // tool"); the glyph itself is the system "+" mark.
  const suggestContent = (
    <div
      style={{
        width: '60%',
        aspectRatio: '1 / 1',
        borderRadius: 18,
        border: `1px dashed ${hover ? VIOLET_LIGHT : VIOLET}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'border-color 0.18s ease',
      }}
    >
      {glyph}
    </div>
  );

  // Live + Soon tools: gradient glyph, soon-state dims it (kept).
  const iconContent = glyph ? (
    <div style={{
      width: '60%',
      height: '60%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: isSoon ? 0.45 : 1,
      transition: 'opacity 0.18s ease',
    }}>
      {glyph}
    </div>
  ) : null;

  // Note: the visual "Launching soon" signal is the blur+dim on the icon
  // (handled by the `isSoon` filter/opacity in iconContent above).
  // The "Launching Soon" header lives on the ComingSoon page itself,
  // shown when the user taps the tile.

  return (
    <Link
      to={tool.path}
      style={{ textDecoration: 'none', color: 'inherit' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div style={cardStyle}>
        {isSuggest ? suggestContent : iconContent}
      </div>
      <div style={{
        marginTop: 12,
        textAlign: 'center',
        fontSize: 'clamp(12px, 3.2vw, 15px)',
        fontWeight: 600,
        color: TEXT,
        fontFamily: FONT,
        // Don't break words mid-letter; let "PercentCalc" stay one line
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
        {tool.name}
      </div>
    </Link>
  );
}

// ─── Main Landing component ────────────────────────────────────────
export default function Landing() {
  return (
    <div style={{
      minHeight: '100vh',
      background: BG,
      color: TEXT,
      fontFamily: FONT,
      position: 'relative',
    }}>
      {/* V45.3 bottom-center radial glow for cross-suite consistency */}
      <div style={{
        position: 'fixed',
        bottom: '-30%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '60vw',
        maxWidth: 600,
        height: '60vw',
        maxHeight: 600,
        background: `radial-gradient(circle, ${VIOLET}1F 0%, transparent 65%)`,
        pointerEvents: 'none',
        borderRadius: '50%',
        zIndex: 0,
      }} />
      <div style={{
        maxWidth: 1100,
        margin: '0 auto',
        padding: '32px 24px 80px',
        // V57.7: bumped from 32px to 64px so the wordmark sits clear
        // of the iPhone dynamic island. env() safe-area inset is added
        // on top, so total clearance is 64px + notch inset.
        paddingTop: 'calc(64px + env(safe-area-inset-top))',
        paddingLeft: 'calc(24px + env(safe-area-inset-left))',
        paddingRight: 'calc(24px + env(safe-area-inset-right))',
        // V60.6: bottom padding reduced from 120 → 30 (still clears the
        // iPhone home indicator safe area, but doesn't leave a huge
        // empty band below the footer). V58.1's 120 was set when the
        // footer was higher up on the page; with the V60.6 footer
        // marginTop also reduced (76 → 24), the relationship between
        // content, footer, and bottom edge is tight without feeling
        // cramped.
        paddingBottom: 'calc(30px + env(safe-area-inset-bottom))',
        position: 'relative',
        zIndex: 1,
      }}>
        {/* ─── Header / wordmark — small top-left badge + SVG wordmark (V45.3) ─── */}
        <header style={{ marginBottom: 32, display: "flex", alignItems: "center", gap: 10 }}>
          <BackArrow to="/" from={VIOLET} toColor={VIOLET_LIGHT} />
          <div style={{
            width: 38, height: 38, borderRadius: 11,
            background: `linear-gradient(135deg, ${VIOLET}, ${VIOLET_LIGHT})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 17, fontWeight: 800, color: "#fff",
          }}>S</div>
          <svg width="115" height="24" viewBox="0 0 115 24" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
            <defs>
              <linearGradient id="wm-stack" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor={VIOLET_LIGHT} />
                <stop offset="1" stopColor="#C4B5FD" />
              </linearGradient>
            </defs>
            <text x="0" y="19" fill="url(#wm-stack)" fontFamily="DM Sans, system-ui, sans-serif" fontSize="20" fontWeight="800" letterSpacing="-0.5">NerdStack</text>
          </svg>
        </header>

        {/* ─── Hero (V45.3 1B pattern — centered + tagline + values line) ─── */}
        <section style={{ marginBottom: 56, textAlign: 'center' }}>
          <h1 style={{
            margin: '0 0 14px',
            fontSize: 'clamp(32px, 6vw, 52px)',
            fontWeight: 800,
            lineHeight: 1.1,
            letterSpacing: '-1.5px',
            color: TEXT,
          }}>
            Study utilities.<br />
            <span style={{
              background: `linear-gradient(135deg, ${VIOLET_LIGHT}, ${VIOLET})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>Free. Fast. Actually good.</span>
          </h1>
          <p style={{
            margin: '0 auto 14px',
            fontSize: 17,
            color: '#9CA3AF',
            fontWeight: 400,
            maxWidth: 580,
            lineHeight: 1.6,
          }}>
            Study utilities by the minute, not the month. GPA, study timer, word count, percent and unit converters — the small things you keep needing, without an app install.
          </p>
          <p style={{
            margin: '0 auto',
            fontSize: 15,
            color: '#6F7290',
            maxWidth: 580,
            lineHeight: 1.6,
          }}>
            Free. No signup. No tracking. No data sale.
          </p>
        </section>

        {/* ─── Tool grid (responsive — matches ToolGrid sizing across NerdSuite) ─── */}
        <section
          aria-label="Tools"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))',
            gap: 28,
          }}
        >
          {TOOLS.map((tool) => (
            <Tile key={tool.path} tool={tool} />
          ))}
        </section>

        {/* ─── Footer ───────────────────────────────────────── */}
        <NerdStackFooter variant="landing" />
      </div>

      {/* Mobile responsive overrides — phone is PINNED to 3 columns
          (locked decision: ToolGrid does the same so NerdStack matches
          the family game grids exactly across viewports) */}
      <style>{`
        @media (max-width: 640px) {
          section[aria-label="Tools"] {
            gap: 12px !important;
          }
        }
        @media (max-width: 480px) {
          /* Phone portrait — pin to 3 columns and tighten gap */
          section[aria-label="Tools"] {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 10px !important;
            margin-left: -8px;
            margin-right: -8px;
          }
          section[aria-label="Tools"] + footer {
            margin-left: -8px;
            margin-right: -8px;
          }
        }
        /* V57.8: keep 3x3 layout on landscape phones too. Without this,
           the auto-fill grid expands to ~6 cols on a 800-900px wide
           landscape viewport and the 9 tiles become 6+3 (or worse). */
        @media (orientation: landscape) and (max-height: 480px) {
          section[aria-label="Tools"] {
            grid-template-columns: repeat(3, 1fr) !important;
            gap: 10px !important;
            max-width: 480px;
            margin: 0 auto !important;
          }
        }
        @media (max-width: 380px) {
          section[aria-label="Tools"] {
            gap: 8px !important;
            margin-left: -12px;
            margin-right: -12px;
          }
        }
      `}</style>
    </div>
  );
}
